import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  Building2,
  CreditCard,
  Loader2,
  AlertTriangle,
  Plus,
  X,
  Star,
  Users,
  FolderOpen,
  FolderKanban,
  ClipboardList,
  ExternalLink,
  MapPin,
  ChevronDown,
  ChevronRight,
  Receipt,
  Paperclip,
  Upload,
  FileText,
  Eye,
  Download,
  Trash2,
} from 'lucide-react';

declare global {
  interface Window {
    Dropbox: {
      choose(options: {
        success: (files: Array<{ name: string; link: string }>) => void;
        linkType: 'preview' | 'direct';
        multiselect: boolean;
        folderselect: boolean;
      }): void;
    };
  }
}
import { api } from '../core/apiClient';
import apiClient from '../core/apiClient';

interface ClientDocumentRow { id: string; name: string; fileName: string; fileSize: number; mimeType?: string; createdAt: string; }

interface ClientEcRow {
  id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; estat: string; createdAt: string;
  budgetsCount?: number; project?: { id: string; projectNumber: string } | null;
}
interface ClientBudgetRow {
  id: string; budgetNumber: string; projecte?: string; budgetDate: string;
  validityDate?: string; estat: string; baseImposable: number;
}
interface ClientInvoiceRow {
  id: string; invoiceNumber: string; issueDate: string; dueDate: string;
  totalAmount: number; pendingAmount: number; status: string;
}

const EC_ESTAT_LABEL: Record<string, string> = {
  OBERT: 'Obert', FACTIBLE: 'Factible', ACCEPTAT: 'Comanda', LLIURAT: 'Lliurat',
  TANCAT: 'Facturat', DESCARTAT: 'Descartat',
};
const BUDGET_ESTAT_LABEL: Record<string, string> = {
  ESBORRANY: 'Obert', ENVIAT: 'Factible', ACCEPTAT: 'Comanda', LLIURAT: 'Lliurat',
  FACTURAT: 'Facturat', REBUTJAT: 'Descartat',
};

interface ClientContact {
  id?: string;
  name: string;
  position?: string;
  phone?: string;
  email?: string;
  unitatTramitadora?: string;
  isPrimary?: boolean;
}

interface ClientAddress {
  id?: string;
  label: string;
  address: string;
  gpsCoordinates?: string;
  isPrimary?: boolean;
}

// Obre Google Maps directament — prioritza les coordenades GPS (mes
// precises) i cau a l'adreça en text lliure si no n'hi ha.
const googleMapsUrl = (a: ClientAddress) =>
  `https://www.google.com/maps?q=${encodeURIComponent((a.gpsCoordinates || a.address).trim())}`;

interface ClientFormData {
  code: string;
  name: string;
  tradeName: string;
  cif: string;
  type: string;
  naturalesaClientId: string;
  rolClientId: string;
  estatComercialId: string;
  idiomaPressupostId: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  contactPerson: string;
  contacts: ClientContact[];
  addresses: ClientAddress[];
  paymentMethod: string;
  paymentDays: number;
  creditLimit: number;
  bankAccount: string;
  ivaRate: number;
  notes: string;
  dropboxUrl: string;
  oficinaComptable: string;
  organGestor: string;
  unitatTramitadora: string;
  organProponent: string;
  billToOtherEntity: boolean;
  billingEntityName: string;
  billingEntityCif: string;
  billingEntityAddress: string;
  billingEntityCity: string;
  billingEntityPostalCode: string;
  billingEntityProvince: string;
}

const initialFormData: ClientFormData = {
  code: '',
  name: '',
  tradeName: '',
  cif: '',
  type: 'EMPRESA',
  naturalesaClientId: '',
  rolClientId: '',
  estatComercialId: '',
  idiomaPressupostId: '',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'CAT',
  phone: '',
  email: '',
  website: '',
  contactPerson: '',
  contacts: [],
  addresses: [],
  paymentMethod: '',
  paymentDays: 30,
  creditLimit: 0,
  bankAccount: '',
  ivaRate: 21,
  notes: '',
  dropboxUrl: '',
  oficinaComptable: '',
  organGestor: '',
  unitatTramitadora: '',
  organProponent: '',
  billToOtherEntity: false,
  billingEntityName: '',
  billingEntityCif: '',
  billingEntityAddress: '',
  billingEntityCity: '',
  billingEntityPostalCode: '',
  billingEntityProvince: '',
};

const countryCodes = [
  { value: 'CAT', label: 'Catalunya' },
  { value: 'ES',  label: 'Espanya' },
  { value: 'FR',  label: 'França' },
  { value: 'PT',  label: 'Portugal' },
  { value: 'IT',  label: 'Itàlia' },
  { value: 'DE',  label: 'Alemanya' },
  { value: 'BE',  label: 'Bèlgica' },
  { value: 'NL',  label: 'Països Baixos' },
  { value: 'AT',  label: 'Àustria' },
  { value: 'PL',  label: 'Polònia' },
  { value: 'CZ',  label: 'República Txeca' },
  { value: 'SK',  label: 'Eslovàquia' },
  { value: 'HU',  label: 'Hongria' },
  { value: 'RO',  label: 'Romania' },
  { value: 'BG',  label: 'Bulgària' },
  { value: 'GR',  label: 'Grècia' },
  { value: 'GB',  label: 'Regne Unit' },
  { value: 'IE',  label: 'Irlanda' },
  { value: 'DK',  label: 'Dinamarca' },
  { value: 'SE',  label: 'Suècia' },
  { value: 'NO',  label: 'Noruega' },
  { value: 'FI',  label: 'Finlàndia' },
  { value: 'LU',  label: 'Luxemburg' },
  { value: 'MT',  label: 'Malta' },
  { value: 'CY',  label: 'Xipre' },
  { value: 'HR',  label: 'Croàcia' },
  { value: 'SI',  label: 'Eslovènia' },
];

export default function ClientForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  // DIR3 (Oficina Comptable / Òrgan Gestor / Unitat Tramitadora / Òrgan Proponent):
  // dir3Names resol codi -> nom oficial (catàleg DIR3); dir3Original és el valor
  // desat en carregar el client (al que torna "Restablir"); dir3Editing controla
  // si cada camp es mostra com a text resolt (+ Editar) o com a input (+ Restablir).
  const [dir3Names, setDir3Names] = useState<Record<string, string>>({});
  const [dir3Original, setDir3Original] = useState<Record<string, string>>({});
  const [dir3Editing, setDir3Editing] = useState<Record<string, boolean>>({});
  const [dir3SearchField, setDir3SearchField] = useState<string | null>(null);
  const [dir3SearchResults, setDir3SearchResults] = useState<{ code: string; name: string }[]>([]);
  const dir3SearchTimeout = useRef<number | null>(null);
  const dir3SearchSeq = useRef(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] = useState<{ id: string; iban: string; bankName: string | null }[]>([]);
  const [clientTypes, setClientTypes] = useState<{ id: number; code: string; name: string; naturalesaClientId?: number | null }[]>([]);
  const [naturalesaClients, setNaturalesaClients] = useState<{ id: number; code: string; name: string }[]>([]);
  const [rolClients, setRolClients] = useState<{ id: number; code: string; name: string }[]>([]);
  const [estatsComercials, setEstatsComercials] = useState<{ id: number; code: string; name: string }[]>([]);
  const [idiomesPressupost, setIdiomesPressupost] = useState<{ id: number; code: string; name: string }[]>([]);
  const [metodesPagament, setMetodesPagament] = useState<{ id: number; name: string }[]>([]);
  const [dropboxReady, setDropboxReady] = useState(false);
  const [condicionsOpen, setCondicionsOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [facturacioOpen, setFacturacioOpen] = useState(false);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [documents, setDocuments] = useState<ClientDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const [ecsOpen, setEcsOpen] = useState(false);
  const [clientEcs, setClientEcs] = useState<ClientEcRow[]>([]);
  const [budgetsOpen, setBudgetsOpen] = useState(false);
  const [clientBudgets, setClientBudgets] = useState<ClientBudgetRow[]>([]);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [clientInvoices] = useState<ClientInvoiceRow[]>([]);

  useEffect(() => {
    api.get<{ success: boolean; data: { appKey: string } }>('/admin/settings/dropbox')
      .then((r) => {
        const key = r.data?.appKey;
        if (!key) return;
        if (document.getElementById('dropboxjs')) { setDropboxReady(true); return; }
        const script = document.createElement('script');
        script.id = 'dropboxjs';
        script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
        script.setAttribute('data-app-key', key);
        script.onload = () => setDropboxReady(true);
        document.head.appendChild(script);
      })
      .catch(() => {});
  }, []);

  const handleDropboxChoose = () => {
    if (!window.Dropbox) return;
    window.Dropbox.choose({
      success: (files) => {
        const file = files[0];
        setFormData(prev => ({ ...prev, dropboxUrl: JSON.stringify({ name: file.name, link: file.link }) }));
      },
      linkType: 'preview',
      multiselect: false,
      folderselect: true,
    });
  };

  // ── Documents adjunts a la fitxa del client ──────────────────────────────

  const fetchDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ success: boolean; data: ClientDocumentRow[] }>(`/clients/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta del formulari si falla el llistat */ }
  }, [id]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // ── Expedients Comercials i Pressupostos del client ──────────────────────

  useEffect(() => {
    if (!id) return;
    api.get<{ success: boolean; data: ClientEcRow[] }>('/expedients-comercials', { clientId: id })
      .then((res) => setClientEcs(res.data ?? []))
      .catch(() => setClientEcs([]));
    api.get<{ success: boolean; data: ClientBudgetRow[] }>('/budgets', { clientId: id })
      .then((res) => setClientBudgets(res.data ?? []))
      .catch(() => setClientBudgets([]));
  }, [id]);

  const handleUploadDocument = async (file: File) => {
    if (!id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/clients/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/clients/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: ClientDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/clients/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const fmtFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('ca-ES') : '—');
  const fmtCurrency = (v: number) => Number(v || 0).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });

  useEffect(() => {
    api.get<{ success: boolean; data: { id: string; iban: string; bankName: string | null }[] }>('/admin/settings/bank-accounts')
      .then((r) => {
        const accounts = r.data ?? [];
        setBankAccounts(accounts);
        if (!isEditing) {
          const caixabank = accounts.find((b) => (b.bankName || '').toUpperCase().replace(/\s+/g, '').includes('CAIXABAN'));
          if (caixabank) setFormData((prev) => (prev.bankAccount ? prev : { ...prev, bankAccount: caixabank.iban }));
        }
      })
      .catch(() => {});
  }, [isEditing]);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; naturalesaClientId?: number | null; isActive: number }[] }>('/catalog/client-types')
      .then((r) => setClientTypes((r.data ?? []).filter((t) => t.isActive)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/naturalesa-client')
      .then((r) => setNaturalesaClients((r.data ?? []).filter((t) => t.isActive)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/rol-client')
      .then((r) => {
        const list = (r.data ?? []).filter((t) => t.isActive);
        setRolClients(list);
        // Client nou: Rol per defecte "Usuari final".
        if (!isEditing) {
          const usuariFinal = list.find((t) => /usuari\s*final/i.test(t.name));
          if (usuariFinal) setFormData((prev) => ({ ...prev, rolClientId: prev.rolClientId || String(usuariFinal.id) }));
        }
      })
      .catch(() => {});
  }, [isEditing]);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/estats-comercials')
      .then((r) => {
        const list = (r.data ?? []).filter((t) => t.isActive);
        setEstatsComercials(list);
        // Client nou: Estat comercial per defecte "Actiu".
        if (!isEditing) {
          const actiu = list.find((t) => /^actiu$/i.test(t.name.trim()));
          if (actiu) setFormData((prev) => ({ ...prev, estatComercialId: prev.estatComercialId || String(actiu.id) }));
        }
      })
      .catch(() => {});
  }, [isEditing]);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/admin/idiomes-pressupost')
      .then((r) => setIdiomesPressupost((r.data ?? []).filter((t) => t.isActive)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; name: string; isActive: number }[] }>('/admin/metodes-pagament')
      .then((r) => {
        const list = (r.data ?? []).filter((m) => m.isActive);
        setMetodesPagament(list);
        // Client nou: Mètode de pagament per defecte "Transferència bancària".
        if (!isEditing) {
          const transferencia = list.find((m) => /transfer[eè]ncia/i.test(m.name));
          if (transferencia) setFormData((prev) => ({ ...prev, paymentMethod: prev.paymentMethod || transferencia.name }));
        }
      })
      .catch(() => {});
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing || !id) return;
    const fetchClient = async () => {
      setLoading(true);
      try {
        const response = await api.get<{ success: boolean; data: any }>(`/clients/${id}`);
        const client = response.data;
        setFormData({
          code:          client.code          || '',
          name:          client.name          || '',
          tradeName:     client.tradeName     || '',
          cif:           client.cif           || '',
          type:          client.type          || 'EMPRESA',
          naturalesaClientId: client.naturalesaClientId ? String(client.naturalesaClientId) : '',
          rolClientId:        client.rolClientId        ? String(client.rolClientId)        : '',
          estatComercialId:   client.estatComercialId   ? String(client.estatComercialId)   : '',
          idiomaPressupostId: client.idiomaPressupostId ? String(client.idiomaPressupostId) : '',
          address:       client.address       || '',
          city:          client.city          || '',
          postalCode:    client.postalCode    || '',
          province:      client.province      || '',
          country:       client.country       || 'CAT',
          phone:         formatPhone(client.phone || ''),
          email:         client.email         || '',
          website:       client.website       || '',
          contactPerson: client.contactPerson || client.contactName || '',
          contacts:      client.contacts      || [],
          addresses:     client.addresses     || [],
          paymentMethod: client.paymentMethod || 'Transferència bancària',
          paymentDays:   client.paymentDays   || client.paymentTerms || 30,
          creditLimit:   client.creditLimit   || 0,
          bankAccount:   client.bankAccount   || '',
          ivaRate:       client.ivaRate       ?? 21,
          notes:              client.notes             || '',
          dropboxUrl:         client.dropboxUrl        || '',
          oficinaComptable:   client.oficinaComptable  || '',
          organGestor:        client.organGestor       || '',
          unitatTramitadora:  client.unitatTramitadora || '',
          organProponent:     client.organProponent    || '',
          billToOtherEntity:      Boolean(client.billToOtherEntity),
          billingEntityName:      client.billingEntityName        || '',
          billingEntityCif:       client.billingEntityCif         || '',
          billingEntityAddress:   client.billingEntityAddress     || '',
          billingEntityCity:      client.billingEntityCity        || '',
          billingEntityPostalCode: client.billingEntityPostalCode || '',
          billingEntityProvince:  client.billingEntityProvince    || '',
        });
        setDir3Original({
          oficinaComptable:  client.oficinaComptable  || '',
          organGestor:       client.organGestor       || '',
          unitatTramitadora: client.unitatTramitadora || '',
          organProponent:    client.organProponent    || '',
        });
        resolveDir3Names([client.oficinaComptable, client.organGestor, client.unitatTramitadora, client.organProponent]);
      } catch {
        setError('Error en carregar el client');
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id, isEditing]);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value }));
  };

  const resolveDir3Names = async (codes: (string | undefined)[]) => {
    const unique = Array.from(new Set(codes.filter((c): c is string => !!c?.trim())));
    if (unique.length === 0) return;
    try {
      const res = await api.get<{ success: boolean; data: Record<string, string> }>(`/dir3-units/resolve?codes=${unique.map(encodeURIComponent).join(',')}`);
      setDir3Names((prev) => ({ ...prev, ...res.data }));
    } catch { /* si falla la resolucio, es mostra nomes el codi */ }
  };

  const startEditDir3 = (field: 'oficinaComptable' | 'organGestor' | 'unitatTramitadora' | 'organProponent') => {
    setDir3Editing((prev) => ({ ...prev, [field]: true }));
  };

  const resetDir3 = (field: 'oficinaComptable' | 'organGestor' | 'unitatTramitadora' | 'organProponent') => {
    setFormData((prev) => ({ ...prev, [field]: dir3Original[field] ?? '' }));
    setDir3Editing((prev) => ({ ...prev, [field]: false }));
  };

  const finishEditDir3 = (field: 'oficinaComptable' | 'organGestor' | 'unitatTramitadora' | 'organProponent') => {
    setDir3Editing((prev) => ({ ...prev, [field]: false }));
    setDir3SearchField(null);
    setDir3SearchResults([]);
    resolveDir3Names([formData[field]]);
  };

  // Buscador per nom dins dels camps DIR3: mentre s'edita, cada tecla llança
  // (amb debounce) una cerca al catàleg i mostra un desplegable de suggeriments.
  // dir3SearchSeq evita que una resposta antiga (d'una lletra anterior) arribi
  // tard i sobreescrigui els resultats d'una cerca més recent.
  const handleDir3Change = (field: 'oficinaComptable' | 'organGestor' | 'unitatTramitadora' | 'organProponent', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setDir3Editing((prev) => ({ ...prev, [field]: true }));
    setDir3SearchField(field);
    if (dir3SearchTimeout.current) window.clearTimeout(dir3SearchTimeout.current);
    const q = value.trim();
    if (q.length < 2) { setDir3SearchResults([]); return; }
    const seq = ++dir3SearchSeq.current;
    dir3SearchTimeout.current = window.setTimeout(async () => {
      try {
        const res = await api.get<{ success: boolean; data: { code: string; name: string }[] }>(`/dir3-units/search?q=${encodeURIComponent(q)}`);
        if (seq === dir3SearchSeq.current) setDir3SearchResults(res.data ?? []);
      } catch { if (seq === dir3SearchSeq.current) setDir3SearchResults([]); }
    }, 250);
  };

  const selectDir3Suggestion = async (field: 'oficinaComptable' | 'organGestor' | 'unitatTramitadora' | 'organProponent', unit: { code: string; name: string }) => {
    setFormData((prev) => ({ ...prev, [field]: unit.code }));
    setDir3Names((prev) => ({ ...prev, [unit.code]: unit.name }));
    setDir3Editing((prev) => ({ ...prev, [field]: false }));
    setDir3SearchField(null);
    setDir3SearchResults([]);

    // En triar l'Oficina Comptable, el seu "parent_code" al catàleg DIR3 és
    // el seu Òrgan Gestor: s'omple automàticament si encara és buit, i Unitat
    // Tramitadora / Òrgan Proponent es predefineixen igual (habitual quan
    // l'organisme no té unitats separades per a cada rol) — sempre editables després.
    if (field !== 'oficinaComptable') return;
    try {
      const res = await api.get<{ success: boolean; data: { code: string; name: string; parentCode?: string; parentName?: string } }>(`/dir3-units/${encodeURIComponent(unit.code)}`);
      const parentCode = res.data?.parentCode;
      const parentName = res.data?.parentName;
      if (!parentCode) return;
      setDir3Names((prev) => (parentName ? { ...prev, [parentCode]: parentName } : prev));
      setFormData((prev) => ({
        ...prev,
        organGestor: prev.organGestor || parentCode,
        unitatTramitadora: prev.unitatTramitadora || parentCode,
        organProponent: prev.organProponent || parentCode,
      }));
    } catch { /* si falla, els altres camps es queden com estaven */ }
  };

  // En triar l'Activitat de client, s'omple automaticament la Naturalesa del
  // client configurada per a aquesta activitat (relacio Admin > Taules >
  // Activitat del Client).
  const handleActivitatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const ct = clientTypes.find((t) => t.code === code);
    setFormData(prev => ({
      ...prev,
      type: code,
      naturalesaClientId: ct?.naturalesaClientId ? String(ct.naturalesaClientId) : prev.naturalesaClientId,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!formData.name?.trim()) { setError('El nom del client es obligatori'); setSaving(false); return; }

      const nullableText = (v?: string) => v?.trim() || undefined;
      const payload: Record<string, any> = {
        name:          formData.name.trim(),
        cif:           nullableText(formData.cif),
        type:          formData.type || undefined,
        tradeName:     nullableText(formData.tradeName),
        address:       nullableText(formData.address),
        city:          nullableText(formData.city),
        postalCode:    nullableText(formData.postalCode),
        province:      nullableText(formData.province),
        country:       nullableText(formData.country),
        phone:         nullableText(formData.phone),
        email:         nullableText(formData.email),
        website:       nullableText(formData.website),
        contactName:   nullableText(formData.contactPerson),
        paymentTerms:  formData.paymentDays ? Number(formData.paymentDays) : undefined,
        paymentMethod: formData.paymentMethod || undefined,
        creditLimit:   formData.creditLimit ? Number(formData.creditLimit) : undefined,
        bankAccount:   nullableText(formData.bankAccount),
        ivaRate:       Number(formData.ivaRate),
        notes:              nullableText(formData.notes),
        dropboxUrl:         nullableText(formData.dropboxUrl),
        oficinaComptable:   nullableText(formData.oficinaComptable),
        organGestor:        nullableText(formData.organGestor),
        unitatTramitadora:  nullableText(formData.unitatTramitadora),
        organProponent:     nullableText(formData.organProponent),
        billToOtherEntity:      formData.billToOtherEntity,
        billingEntityName:      formData.billToOtherEntity ? nullableText(formData.billingEntityName) : undefined,
        billingEntityCif:       formData.billToOtherEntity ? nullableText(formData.billingEntityCif) : undefined,
        billingEntityAddress:   formData.billToOtherEntity ? nullableText(formData.billingEntityAddress) : undefined,
        billingEntityCity:      formData.billToOtherEntity ? nullableText(formData.billingEntityCity) : undefined,
        billingEntityPostalCode: formData.billToOtherEntity ? nullableText(formData.billingEntityPostalCode) : undefined,
        billingEntityProvince:  formData.billToOtherEntity ? nullableText(formData.billingEntityProvince) : undefined,
      };
      if (formData.naturalesaClientId) payload.naturalesaClientId = Number(formData.naturalesaClientId);
      if (formData.rolClientId)        payload.rolClientId        = Number(formData.rolClientId);
      if (formData.estatComercialId)   payload.estatComercialId   = Number(formData.estatComercialId);
      if (formData.idiomaPressupostId) payload.idiomaPressupostId = Number(formData.idiomaPressupostId);
      if (formData.code)     payload.code     = formData.code;
      if (formData.contacts?.length > 0) payload.contacts = formData.contacts;
      if (formData.addresses?.length > 0) payload.addresses = formData.addresses;

      Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });

      if (isEditing && id) {
        await api.put(`/clients/${id}`, payload);
      } else {
        await api.post('/clients', payload);
      }
      navigate('/crm');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Error en guardar el client');
    } finally {
      setSaving(false);
    }
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', position: '', phone: '', email: '', unitatTramitadora: '', isPrimary: prev.contacts.length === 0 }],
    }));
  };

  const removeContact = (index: number) => {
    const updated = formData.contacts.filter((_, i) => i !== index);
    if (formData.contacts[index].isPrimary && updated.length > 0) updated[0].isPrimary = true;
    setFormData(prev => ({ ...prev, contacts: updated }));
  };

  const updateContact = (index: number, field: keyof ClientContact, value: string | boolean) => {
    const updated = [...formData.contacts];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, contacts: updated }));
  };

  const setPrimaryContact = (index: number) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.map((c, i) => ({ ...c, isPrimary: i === index })) }));
  };

  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, { label: '', address: '', gpsCoordinates: '', isPrimary: prev.addresses.length === 0 }],
    }));
  };

  const removeAddress = (index: number) => {
    const updated = formData.addresses.filter((_, i) => i !== index);
    if (formData.addresses[index].isPrimary && updated.length > 0) updated[0].isPrimary = true;
    setFormData(prev => ({ ...prev, addresses: updated }));
  };

  const updateAddress = (index: number, field: keyof ClientAddress, value: string | boolean) => {
    const updated = [...formData.addresses];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, addresses: updated }));
  };

  const setPrimaryAddress = (index: number) => {
    setFormData(prev => ({ ...prev, addresses: prev.addresses.map((a, i) => ({ ...a, isPrimary: i === index })) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Capçalera sticky — fons blanc sòlid i sense icona de tornar, ja que
          "Cancel·lar" a la dreta fa la mateixa funció (estàndard de totes les
          fitxes de l'ERP, 2026-11). */}
      <div className="sticky top-0 z-20 bg-white border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              {isEditing ? (formData.name || 'Editar Client') : 'Nou Client'}
            </h1>
            {isEditing && formData.code && (
              <p className="text-xs text-muted-foreground">{formData.code} | CIF: {formData.cif}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={() => navigate('/crm')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <button type="submit" form="client-form" disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Desant...' : 'Desa'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form id="client-form" onSubmit={handleSubmit} className="space-y-6 text-[12px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Columna esquerra — Dades del Client */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Building2 size={16} className="text-blue-600" /> Dades del Client
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Codi</label>
                  <input type="text" name="code" value={formData.code} onChange={handleChange}
                    placeholder="Auto si buit"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">CIF/NIF *</label>
                  <input type="text" name="cif" value={formData.cif} onChange={handleChange} required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Activitat de client</label>
                  <select name="type" value={formData.type} onChange={handleActivitatChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Selecciona...</option>
                    {clientTypes.map(ct => (
                      <option key={ct.code} value={ct.code}>{ct.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Naturalesa del client</label>
                  <select name="naturalesaClientId" value={formData.naturalesaClientId} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Selecciona...</option>
                    {naturalesaClients.map(nc => (
                      <option key={nc.id} value={nc.id}>{nc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Rol del client</label>
                  <select name="rolClientId" value={formData.rolClientId} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Selecciona...</option>
                    {rolClients.map(rc => (
                      <option key={rc.id} value={rc.id}>{rc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Estat comercial</label>
                  <select name="estatComercialId" value={formData.estatComercialId} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Selecciona...</option>
                    {estatsComercials.map(ec => (
                      <option key={ec.id} value={ec.id}>{ec.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Nom / Raó Social *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Nom Comercial</label>
                  <input type="text" name="tradeName" value={formData.tradeName} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Telèfon General</label>
                  <div className="flex gap-1">
                    <input type="tel" name="phone" value={formData.phone} placeholder="670 435 212"
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    {formData.phone && (
                      <a href={`tel:${formData.phone.replace(/\s/g, '')}`}
                        className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Trucar">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Email General</label>
                  <div className="flex gap-1">
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    {formData.email && (
                      <a href={`mailto:${formData.email}`}
                        className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Enviar email">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Web</label>
                  <div className="flex gap-1">
                    <input type="url" name="website" value={formData.website} onChange={handleChange}
                      placeholder="https://www.exemple.com"
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    {formData.website && (
                      <a href={formData.website} target="_blank" rel="noopener noreferrer"
                        className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Obrir web">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Idioma</label>
                  <select name="idiomaPressupostId" value={formData.idiomaPressupostId} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Selecciona...</option>
                    {idiomesPressupost.map(ip => (
                      <option key={ip.id} value={ip.id}>{ip.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Adreça</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1">Ciutat</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1">Província</label>
                  <input type="text" name="province" value={formData.province} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">CP</label>
                  <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">País</label>
                  <select name="country" value={formData.country} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    {countryCodes.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Columna dreta — Condicions + Contactes */}
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <button type="button" onClick={() => setCondicionsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {condicionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <CreditCard size={16} className="text-green-600" /> Condicions Comercials
                </button>
              </div>
              {condicionsOpen && (
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Mètode de Pagament</label>
                    <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      <option value="">— Selecciona —</option>
                      {/* Valor antic no present al catàleg actual — es mostra tal qual
                          en lloc d'amagar-lo. */}
                      {formData.paymentMethod && !metodesPagament.some(m => m.name === formData.paymentMethod) && (
                        <option value={formData.paymentMethod}>{formData.paymentMethod}</option>
                      )}
                      {metodesPagament.map(m => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Dies de Pagament</label>
                    <input type="number" name="paymentDays" value={formData.paymentDays} onChange={handleChange} min={0}
                      className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">IVA</label>
                    <select name="ivaRate" value={formData.ivaRate}
                      onChange={(e) => setFormData(prev => ({ ...prev, ivaRate: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      <option value="21">21,00 %</option>
                      <option value="10">10,00 %</option>
                      <option value="0">0,00 %</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Límit de Crèdit (€)</label>
                    <input type="number" name="creditLimit" value={formData.creditLimit} onChange={handleChange} min={0} step={100}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">IBAN (compte Roig i Fills)</label>
                    <select name="bankAccount" value={formData.bankAccount} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      <option value="">— Sense compte —</option>
                      {bankAccounts.map((b) => (
                        <option key={b.id} value={b.iban}>
                          {b.iban}{b.bankName ? ` — ${b.bankName}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Localització Dropbox</label>
                  <div className="flex gap-1.5">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 text-xs min-h-[34px] overflow-hidden">
                      {formData.dropboxUrl ? (() => {
                        try {
                          const loc: { name: string; link: string } = JSON.parse(formData.dropboxUrl);
                          return (<><FolderOpen className="h-4 w-4 text-blue-500 shrink-0" /><span className="truncate">{loc.name}</span></>);
                        } catch { return <span className="text-muted-foreground truncate">{formData.dropboxUrl}</span>; }
                      })() : <span className="text-muted-foreground">Cap carpeta seleccionada</span>}
                    </div>
                    {dropboxReady && (
                      <button type="button" onClick={handleDropboxChoose}
                        className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors text-xs flex items-center gap-1.5 shrink-0">
                        <FolderOpen className="h-4 w-4" />Seleccionar
                      </button>
                    )}
                    {formData.dropboxUrl && (() => {
                      try {
                        const loc: { name: string; link: string } = JSON.parse(formData.dropboxUrl);
                        return loc.link ? (
                          <a href={loc.link} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors flex items-center shrink-0" title="Obrir a Dropbox">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : null;
                      } catch { return null; }
                    })()}
                    {formData.dropboxUrl && (
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, dropboxUrl: '' }))}
                        className="px-2 py-2 border rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-red-500 shrink-0">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Dades de facturació — quan el client no te NIF propi (ex: museus)
                i la factura ha d'anar a nom d'un altre organisme */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <button type="button" onClick={() => setFacturacioOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {facturacioOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Receipt size={16} className="text-amber-600" /> Dades de facturació
                </button>
              </div>
              {facturacioOpen && (
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {([
                      ['oficinaComptable', 'Oficina Comptable'],
                      ['organGestor', 'Òrgan Gestor'],
                      ['unitatTramitadora', 'Unitat Tramitadora'],
                      ['organProponent', 'Òrgan Proponent'],
                    ] as const).map(([field, label]) => {
                      const value = formData[field];
                      const editing = dir3Editing[field] || !value;
                      const resolvedName = dir3Names[value];
                      const showDropdown = editing && dir3SearchField === field && dir3SearchResults.length > 0;
                      return (
                        <div key={field} className="relative">
                          <label className="block text-xs font-medium mb-1">{label}</label>
                          {editing ? (
                            <input
                              type="text"
                              name={field}
                              autoFocus={dir3Editing[field]}
                              value={value}
                              onChange={(e) => handleDir3Change(field, e.target.value)}
                              onFocus={() => value.trim().length >= 2 && setDir3SearchField(field)}
                              onBlur={() => finishEditDir3(field)}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') resetDir3(field); }}
                              placeholder="Codi DIR3 o nom de l'organisme"
                              autoComplete="off"
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          ) : (
                            <div
                              onClick={() => startEditDir3(field)}
                              title={resolvedName ? `${value} - ${resolvedName} (clica per editar)` : `${value} (clica per editar)`}
                              className="w-full px-3 py-2 border rounded-lg bg-muted/30 text-sm truncate cursor-text hover:bg-muted transition-colors"
                            >
                              {resolvedName ? `${value} - ${resolvedName}` : value}
                            </div>
                          )}
                          {showDropdown && (
                            <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border rounded-lg shadow-lg text-xs">
                              {dir3SearchResults.map((unit) => (
                                <li key={unit.code}>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectDir3Suggestion(field, unit)}
                                    className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                                  >
                                    <span className="font-mono text-muted-foreground">{unit.code}</span> — {unit.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {value && dir3Editing[field] && (
                            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => resetDir3(field)}
                              className="mt-1 px-2 py-1 text-xs border rounded hover:bg-muted transition-colors">
                              Restablir
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.billToOtherEntity}
                      onChange={(e) => setFormData(prev => ({ ...prev, billToOtherEntity: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Facturar a una altra entitat
                  </label>
                  {formData.billToOtherEntity && (
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground">
                        Els documents (pressupostos, factures) es faran a nom d'aquesta entitat en lloc del client.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium mb-1">Nom de l'entitat</label>
                          <input type="text" name="billingEntityName" value={formData.billingEntityName} onChange={handleChange}
                            placeholder="Ex: Ajuntament de..."
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">CIF/NIF</label>
                          <input type="text" name="billingEntityCif" value={formData.billingEntityCif} onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Adreça</label>
                        <input type="text" name="billingEntityAddress" value={formData.billingEntityAddress} onChange={handleChange}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium mb-1">Ciutat</label>
                          <input type="text" name="billingEntityCity" value={formData.billingEntityCity} onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">CP</label>
                          <input type="text" name="billingEntityPostalCode" value={formData.billingEntityPostalCode} onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Província</label>
                          <input type="text" name="billingEntityProvince" value={formData.billingEntityProvince} onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Contactes compactes */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <button type="button" onClick={() => setContactsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {contactsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Users size={16} className="text-blue-600" /> Persones de Contacte
                  {formData.contacts.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({formData.contacts.length})</span>
                  )}
                </button>
                <button type="button" onClick={addContact}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                  <Plus className="h-3 w-3" />Afegir
                </button>
              </div>
              {contactsOpen && (
              <div className="p-5">
                {formData.contacts.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">Cap contacte afegit.</p>
                ) : (
                  <div className="space-y-2 max-h-[220px] overflow-y-auto">
                    {formData.contacts.map((contact, index) => (
                      <div key={index} className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-background">
                        <input type="text" value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                          placeholder="Nom"
                          className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                        <input type="text" value={contact.position || ''}
                          onChange={(e) => updateContact(index, 'position', e.target.value)}
                          placeholder="Càrrec"
                          className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                        <div className="flex gap-1 items-center">
                          <input type="tel" value={contact.phone || ''}
                            onChange={(e) => updateContact(index, 'phone', e.target.value)}
                            placeholder="Telèfon"
                            className="w-28 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          {contact.phone && (
                            <a href={`tel:${contact.phone.replace(/\s/g, '')}`}
                              className="text-muted-foreground hover:text-primary transition-colors" title="Trucar">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex gap-1 items-center flex-1 min-w-0">
                          <input type="email" value={contact.email || ''}
                            onChange={(e) => updateContact(index, 'email', e.target.value)}
                            placeholder="Email"
                            className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          {contact.email && (
                            <a href={`mailto:${contact.email}`}
                              className="text-muted-foreground hover:text-primary transition-colors shrink-0" title="Enviar email">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                        <input type="text" value={contact.unitatTramitadora || ''}
                          onChange={(e) => updateContact(index, 'unitatTramitadora', e.target.value)}
                          placeholder="Unitat Tramitadora"
                          className="w-32 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                        <div className="flex items-center gap-1 shrink-0">
                          {contact.isPrimary ? (
                            <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                          ) : (
                            <button type="button" onClick={() => setPrimaryContact(index)}
                              className="p-1 hover:bg-muted rounded" title="Fer principal">
                              <Star className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                          )}
                          <button type="button" onClick={() => removeContact(index)}
                            className="p-1 hover:bg-destructive/10 text-destructive rounded" title="Eliminar">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Adreces (Entrega, Magatzem, Fiscal...) */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <button type="button" onClick={() => setAddressesOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {addressesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <MapPin size={16} className="text-orange-600" /> Adreces
                  {formData.addresses.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({formData.addresses.length})</span>
                  )}
                </button>
                <button type="button" onClick={addAddress}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                  <Plus className="h-3 w-3" />Afegir
                </button>
              </div>
              {addressesOpen && (
              <div className="p-5">
                {formData.addresses.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">Cap adreça afegida.</p>
                ) : (
                  <div className="space-y-2 max-h-[220px] overflow-y-auto">
                    {formData.addresses.map((addr, index) => (
                      <div key={index} className="border rounded-lg px-3 py-2 bg-background space-y-1.5">
                        <div className="flex items-center gap-2">
                          <input type="text" value={addr.label}
                            onChange={(e) => updateAddress(index, 'label', e.target.value)}
                            placeholder="Ex: Entrega, Magatzem..."
                            className="w-32 shrink-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          <div className="flex items-center gap-1 ml-auto shrink-0">
                            {addr.isPrimary ? (
                              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                            ) : (
                              <button type="button" onClick={() => setPrimaryAddress(index)}
                                className="p-1 hover:bg-muted rounded" title="Fer principal">
                                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                            )}
                            <button type="button" onClick={() => removeAddress(index)}
                              className="p-1 hover:bg-destructive/10 text-destructive rounded" title="Eliminar">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <input type="text" value={addr.address}
                          onChange={(e) => updateAddress(index, 'address', e.target.value)}
                          placeholder="Adreça"
                          className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                        <div className="flex items-center gap-2">
                          <input type="text" value={addr.gpsCoordinates || ''}
                            onChange={(e) => updateAddress(index, 'gpsCoordinates', e.target.value)}
                            placeholder="Coordenades GPS (lat,lng)"
                            className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          {(addr.gpsCoordinates || addr.address) && (
                            <a href={googleMapsUrl(addr)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 border rounded text-xs text-muted-foreground hover:text-primary hover:border-primary transition-colors shrink-0"
                              title="Obrir a Google Maps">
                              <ExternalLink className="h-3 w-3" /> Maps
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Expedients Comercials del client */}
            {isEditing && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <button type="button" onClick={() => setEcsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {ecsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <FolderKanban size={16} className="text-purple-600" /> Expedients Comercials
                  {clientEcs.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({clientEcs.length})</span>
                  )}
                </button>
                <button type="button" onClick={() => navigate(`/crm/expedients-comercials/new?clientId=${id}`)}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                  <Plus className="h-3 w-3" />Afegir
                </button>
              </div>
              {ecsOpen && (
              <div className="p-5">
                {clientEcs.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">Cap expedient comercial registrat.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-muted-foreground">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-gray-100">
                          <th className="py-1.5 font-medium">Número</th>
                          <th className="py-1.5 font-medium">Nom</th>
                          <th className="py-1.5 font-medium">Estat</th>
                          <th className="py-1.5 font-medium">Data</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientEcs.map((ec) => (
                          <tr key={ec.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/crm/expedients-comercials/${ec.id}/edit`)}>
                            <td className="py-1.5 font-medium">{ec.ecNumber}</td>
                            <td className="py-1.5 truncate max-w-[200px]">{ec.etiquetaExpedient || ec.name}</td>
                            <td className="py-1.5">{EC_ESTAT_LABEL[ec.estat] ?? ec.estat}</td>
                            <td className="py-1.5">{fmtDate(ec.createdAt)}</td>
                            <td className="py-1.5 text-right"><ExternalLink className="h-3.5 w-3.5 text-muted-foreground inline" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Pressupostos del client */}
            {isEditing && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <button type="button" onClick={() => setBudgetsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {budgetsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <ClipboardList size={16} className="text-indigo-600" /> Pressupostos
                  {clientBudgets.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({clientBudgets.length})</span>
                  )}
                </button>
                <button type="button" onClick={() => navigate(`/crm/pressupostos/new?clientId=${id}`)}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                  <Plus className="h-3 w-3" />Afegir
                </button>
              </div>
              {budgetsOpen && (
              <div className="p-5">
                {clientBudgets.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">Cap pressupost registrat.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-muted-foreground">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-gray-100">
                          <th className="py-1.5 font-medium">Número</th>
                          <th className="py-1.5 font-medium">Projecte</th>
                          <th className="py-1.5 font-medium">Data</th>
                          <th className="py-1.5 font-medium">Estat</th>
                          <th className="py-1.5 font-medium text-right">Import</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientBudgets.map((b) => (
                          <tr key={b.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/crm/pressupostos/${b.id}/edit`)}>
                            <td className="py-1.5 font-medium">{b.budgetNumber}</td>
                            <td className="py-1.5 truncate max-w-[160px]">{b.projecte || '—'}</td>
                            <td className="py-1.5">{fmtDate(b.budgetDate)}</td>
                            <td className="py-1.5">{BUDGET_ESTAT_LABEL[b.estat] ?? b.estat}</td>
                            <td className="py-1.5 text-right font-mono">{fmtCurrency(b.baseImposable)}</td>
                            <td className="py-1.5 text-right"><ExternalLink className="h-3.5 w-3.5 text-muted-foreground inline" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Historial de facturació del client */}
            {isEditing && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <button type="button" onClick={() => setInvoicesOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {invoicesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Receipt size={16} className="text-teal-600" /> Historial de facturació
                  {clientInvoices.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({clientInvoices.length})</span>
                  )}
                </button>
              </div>
              {invoicesOpen && (
              <div className="p-5">
                {clientInvoices.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">Encara no hi ha factures per aquest client.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-gray-100">
                          <th className="py-1.5 font-medium">Número</th>
                          <th className="py-1.5 font-medium">Emissió</th>
                          <th className="py-1.5 font-medium">Venciment</th>
                          <th className="py-1.5 font-medium text-right">Import</th>
                          <th className="py-1.5 font-medium text-right">Pendent</th>
                          <th className="py-1.5 font-medium">Estat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientInvoices.map((inv) => (
                          <tr key={inv.id} className="border-b border-gray-50 last:border-0">
                            <td className="py-1.5 font-medium">{inv.invoiceNumber}</td>
                            <td className="py-1.5">{fmtDate(inv.issueDate)}</td>
                            <td className="py-1.5">{fmtDate(inv.dueDate)}</td>
                            <td className="py-1.5 text-right font-mono">{fmtCurrency(inv.totalAmount)}</td>
                            <td className="py-1.5 text-right font-mono">{fmtCurrency(inv.pendingAmount)}</td>
                            <td className="py-1.5">{inv.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Documents adjunts a la fitxa del client */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <button type="button" onClick={() => setDocumentsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                  {documentsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Paperclip size={16} className="text-amber-600" /> Documents generals Client
                  {documents.length > 0 && (
                    <span className="text-xs font-normal text-muted-foreground">({documents.length})</span>
                  )}
                </button>
              </div>
              {documentsOpen && (
              <div className="p-5">
                {!isEditing ? (
                  <p className="text-muted-foreground text-xs text-center py-4">Desa el client abans de pujar documents.</p>
                ) : (
                  <>
                    <label
                      onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
                      onDragLeave={() => setDocsDragOver(false)}
                      onDrop={handleDocumentsDrop}
                      className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${
                        docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Upload size={18} className="text-gray-400" />
                      <span className="text-xs text-muted-foreground">
                        {uploadingDoc ? 'Pujant...' : 'Arrossega o clica per pujar un document'}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        disabled={uploadingDoc}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadDocument(file);
                          e.target.value = '';
                        }}
                      />
                    </label>

                    {documents.length === 0 ? (
                      <p className="text-muted-foreground text-xs text-center py-3">Encara no hi ha cap document</p>
                    ) : (
                      <div className="mt-3 space-y-1">
                        {documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                            <FileText size={14} className="text-gray-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="truncate font-medium" title={doc.name}>{doc.name}</div>
                              <div className="text-muted-foreground">{fmtFileSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString('ca-ES')}</div>
                            </div>
                            <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                              <Eye size={14} />
                            </button>
                            <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors">
                              <Download size={14} />
                            </button>
                            <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
